import * as React from "react";
import {useEffect} from "react";
import * as Sentry from "@sentry/react";
import {evaluateLoginCode, hasToken, isProcessingLoginCode, openLoginPage} from "../Utils/authentication";
import {IContent, IModulePage} from "./IHub";
import {HubPathRouting} from "./pathBuilder";
import {cleansePageName} from "../Utils/transformer";
import {useMain} from "../Utils/SessionControls/mainContext";
import {Outlet, useLocation, useNavigate} from "react-router-dom";
import OdxHeader from "../OdxComponents/NativeOdx/Odx-Header";
import {isUserroleSufficient} from "../Utils/authorization";
import MainLayout from "../OdxComponents/CustomOdx/MainLayout";


interface IHubFrameworkProps {
    hubContent: IContent,
    module: unknown
}

export const HubFramework: React.FC<IHubFrameworkProps> = ({hubContent, module}) => {
    const [currentPage, setCurrentPage] = React.useState<IModulePage>();

    const {isLoading, setLoading, getToast, createErrorToast} = useMain()

    const loginStartedRef = React.useRef(false);


    const navigate = useNavigate();
    let location = useLocation();

    useEffect(() => {
        setLoading(true);
        checkLogin()
        // redirect to the overview page if no page is set
        if (getPageFromUrl() === "" || window.location.href.includes("#Hub")) {
            navigate("/" + cleansePageName(hubContent.pages[0].displayName))
        }

    }, [])

    useEffect(() => {
        if (currentPage && currentPage.displayName === "") {
            navigate("/" + cleansePageName(hubContent.pages[0].displayName), {})
        }
    }, [currentPage]);

    useEffect(() => {
        // if no page is set, set it to first page of module
        const pageFromURL = hubContent.pages.filter((p: IModulePage) => cleansePageName(p.displayName) === getPageFromUrl())[0]
        if (!pageFromURL) {
          navigate(cleansePageName(hubContent.pages[0].displayName), {relative: "path", replace: true})
          return
        }

        const newPage = hubContent.pages.filter(
            (p: IModulePage) => cleansePageName(p.displayName) === location.pathname.split("/").pop())[0]
        if (newPage && newPage.displayName !== currentPage?.displayName) {
            setTabState(newPage.displayName)
        }
    }, [location]);

    const checkLogin = async () => {
        if (isProcessingLoginCode) return;
        const evaluateState = await evaluateLoginCode();

        if (evaluateState.error !== "") {
            createErrorToast(evaluateState.error);

        } else if (!evaluateState.success) {
            openLoginPage()
        }
    }

    const setTabState = async (tab: string) => {
        let newPage = hubContent.pages.filter(
            (p: IModulePage) => p.displayName === tab || p.url === tab)[0]

        if (!newPage) {
            createErrorToast("Tab not found. Please look into module config.")
            return
        }
        HubPathRouting.currentPage = {moduleName: hubContent.moduleName, displayName: newPage.displayName}

        setCurrentPage(newPage)
    }


    const getPageFromUrl = () => {
        let url = window.location.href
        let urlPart = url.split("?")[0]
        let page = urlPart.split("/").pop()
        return page
    }

    let loading = <div></div>;
    if (isLoading) {
        loading = (
            <div
                id="loadingSpinner"
                className="fixed inset-0 z-[2000] flex items-center justify-center bg-white/70 backdrop-blur-sm"
                style={{top: "55px", left: "240px"}}
            >
                <odx-loading-spinner>
                    <span>Loading data</span>
                </odx-loading-spinner>
            </div>
        )
    }

    if (!hasToken()) {
        return(
            <></>
        );
    }

    return (
        <Sentry.ErrorBoundary
            fallback={({error, componentStack, resetError}) => (
                <div>
                    <p>An error has occurred: {error.toString()}</p>
                    <br></br>
                    <p>{componentStack}</p>
                </div>
            )}
        >
            <div className="odx-app-root">
                <div className="odx-app odx-loading-spinner-parent">
                    {loading}
                    <div className={`odx-toast-container fixed inset-0 !top-[50px] w-fit h-fit m-auto overflow-auto z-[1000] text-[canvastext] bg-[canvas] border p-[0.25em]`}>
                        {getToast()}
                    </div>

                    <OdxHeader/>
                    <MainLayout
                        currentPage={currentPage}
                        hubContent={hubContent}
                        className={"h-[calc(100vh-48px)] !select-text"}
                        outlet={
                            currentPage && isUserroleSufficient(currentPage.minimumRole, HubPathRouting.currentProject?.id) ? (
                                <Outlet context={module}/>
                            ) : null
                        }
                    />
                </div>
            </div>
        </Sentry.ErrorBoundary>
    );
}
